/**
 * R2 trace key parsing — mirrors the server's parse_trace_key
 * (api/app/services/r2_client.py), its _TRACE_KEY_RE and its calendar
 * check, so both sides classify a key identically.
 */

const TRACE_KEY_RE =
  /^traces\/[^/]+\/(?<year>\d{4})\/(?<month>\d{2})\/(?<day>\d{2})\/(?:(?<conv>[^/]+)\/)?(?<trace>[^/]+)\.json$/

export interface ParsedTraceKey {
  /** ISO date (YYYY-MM-DD) from the key's path. */
  date: string
  /**
   * The client's conversation id, decoded: what the gateway's conversation
   * rows and by-id filters carry.
   */
  conversationId: string | null
  /**
   * The conversation SEGMENT of the key, still percent-encoded the way the
   * gateway wrote it (`getTraceKey` uses encodeURIComponent). This is what
   * the api puts in a queue row's `conversation_id`, so compare it against
   * other keys and queue rows, never against a decoded id.
   */
  conversationSegment: string | null
  /** The trace id the key's file name carries (TRA-1079). */
  traceId: string
}

/** The server rejects a key whose date is not on the calendar (February
    30), so it is malformed here too. */
function isCalendarDate(year: string, month: string, day: string): boolean {
  const y = Number(year)
  const m = Number(month) - 1
  const d = Number(day)
  const date = new Date(0)
  date.setUTCFullYear(y, m, d)
  return y >= 1 && date.getUTCFullYear() === y && date.getUTCMonth() === m && date.getUTCDate() === d
}

/** Null on a malformed key — the caller says so rather than guessing. */
export function parseTraceKey(key: string): ParsedTraceKey | null {
  const groups = TRACE_KEY_RE.exec(key)?.groups
  if (groups === undefined) return null
  const { year, month, day, conv, trace } = groups
  if (year === undefined || month === undefined || day === undefined || trace === undefined) {
    return null
  }
  if (!isCalendarDate(year, month, day)) return null
  try {
    return {
      date: `${year}-${month}-${day}`,
      conversationId: conv === undefined ? null : decodeURIComponent(conv),
      conversationSegment: conv ?? null,
      traceId: trace,
    }
  } catch (error) {
    if (error instanceof URIError) return null
    throw error
  }
}
